import { captureException } from '@sentry/react';
import { NotificationDispatch } from '@tryhackme/thm-ui-components';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useFeatureIsOn } from 'src/common/growthbook';
import { useGetUserQuery } from 'src/common/slices';
import { useGoogleOneTapAuth } from './hooks/use-google-one-tap-auth';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;
const GOOGLE_ONE_TAP_FEATURE_FLAG_NAME = 'google-1-tap';
const GOOGLE_ONE_TAP_FREE_ROOMS_FEATURE_FLAG_NAME = 'google-tap-free-rooms';
const LANDING_PAGE_PATH = '/';
const FREE_ROOM_PARTIAL_PATH = '/room/';
interface GoogleAuthError {
  data?: {
    message?: string;
  };
}
export const GoogleOneTap = () => {
  const {
    data: user
  } = useGetUserQuery();
  const handleAuth = useGoogleOneTapAuth();
  const landingPageFlag = useFeatureIsOn(GOOGLE_ONE_TAP_FEATURE_FLAG_NAME);
  const freeRoomsFlag = useFeatureIsOn(GOOGLE_ONE_TAP_FREE_ROOMS_FEATURE_FLAG_NAME);
  const location = useLocation();
  const isAuthenticatedRef = useRef(false);
  useEffect(() => {
    const handleAuthEvent = () => {
      isAuthenticatedRef.current = true;
      // Re-initialize Google One Tap to clear the prompt
      if (window.google?.accounts?.id && GOOGLE_CLIENT_ID) {
        window.google.accounts.id.cancel();
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: () => {} // Empty callback since we're just clearing the prompt
        });
      }
    };
    window.addEventListener('googleOneTapAuthenticated', handleAuthEvent);
    return () => {
      window.removeEventListener('googleOneTapAuthenticated', handleAuthEvent);
    };
  }, []);
  useEffect(() => {
    const isFreeRoomsPage = location.pathname.startsWith(FREE_ROOM_PARTIAL_PATH) && freeRoomsFlag;
    const isLandingPage = location.pathname === LANDING_PAGE_PATH && landingPageFlag;
    const shouldShow = (isLandingPage || isFreeRoomsPage) && !user && !isAuthenticatedRef.current && !!window.google?.accounts?.id;
    if (shouldShow && GOOGLE_CLIENT_ID && window.google?.accounts?.id) {
      const nonce = window.crypto?.randomUUID() || Math.random().toString(36).slice(2);
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        nonce,
        callback: response => {
          if (response.credential) {
            handleAuth(response.credential, nonce).then(() => {
              if (isFreeRoomsPage) {
                window.dispatchEvent(new CustomEvent('googleOneTapAuthenticated'));
              }
            }).catch((error: GoogleAuthError) => {
              NotificationDispatch('error', error?.data?.message || 'Please try again later or use email/google sign in.');
              captureException(error);
            });
          }
        },
        cancel_on_tap_outside: false
      });
      window.google.accounts.id.prompt();
    }
    return () => {
      window.google?.accounts?.id?.cancel();
    };
  }, [freeRoomsFlag, landingPageFlag, location.pathname, user, handleAuth]);
  return <div id="google-one-tap-container" style={{
    position: 'fixed',
    top: 0,
    right: 0,
    zIndex: 9999
  }} data-sentry-component="GoogleOneTap" data-sentry-source-file="google-one-tap.tsx" />;
};